import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { CircleExclamation } from '@unified-checkout/design-system/icons/index';

import type { TReportTemplateTypes } from '@app/dashboard/types/shared/modals/reports-template';
import {
  downloadProofOfTransfer,
  exportSingleTransferReport,
} from '@app/dashboard/services/inner_layout/balance_and_transfers/single-transfer';
import { TWO_SECONDS } from '@app/dashboard/constants/shared';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';
import { downloadFile } from '@app/dashboard/utils/downloadFile';
import { notify } from '@app/dashboard/utils/notifier';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';

export const useTransferActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const downloadAch = useMutation({
    mutationFn: (id: string) => downloadProofOfTransfer(id),
    onSuccess: (res) => {
      downloadFile({
        res,
        defaultFilename: `proof-of-transfer`,
        type: 'pdf',
      });
    },
    onError: (response) => {
      notify.error(response.message, { icon: CircleExclamation });
    },
  });

  const exportTransferReport = useMutation({
    mutationFn: ({
      id,
      fileFormat,
      templateType,
    }: {
      id: string;
      fileFormat: 'pdf' | 'xlsx' | 'csv';
      templateType: TReportTemplateTypes;
    }) => {
      const today = new Date();
      const formattedDate = formatToISODate(today);

      return exportSingleTransferReport({
        id,
        startDate: formattedDate,
        endDate: formattedDate,
        fileFormat,
        reportType: 'transactions',
        templateType,
      });
    },
    onSuccess: () => {
      notify.success(
        t(
          'innerLayout.balancePage.transfersPage.singleTransferPage.exportSuccessMessage'
        )
      );

      setTimeout(() => {
        navigate(INNER_ROUTES.REPORTS);
      }, TWO_SECONDS);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('errors.somethingWentWrong');

      notify.error(errorMessage);
    },
  });

  return {
    exportTransferReport,
    downloadAch,
  };
};
